// Installing the POS as an app.
//
// Chrome (and Edge/Samsung Internet) announce that the page can be installed
// with a one-time `beforeinstallprompt` event. It used to be captured only by
// the install banner, and only while that banner had not been dismissed, so a
// tablet where someone once tapped "Not now" had no install option anywhere.
// The event is now kept here from page load, for both the banner and the
// header menu's "Install app".

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

export const captureInstallPrompt = (event) => {
  // Stops Chrome's own mini-infobar; we offer the install ourselves.
  event.preventDefault();
  deferredPrompt = event;
  notify();
};

export const clearInstallPrompt = () => {
  deferredPrompt = null;
  notify();
};

export const canPromptInstall = () => Boolean(deferredPrompt);

export const subscribeInstallPrompt = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Shows the browser's install dialog. Resolves to 'accepted', 'dismissed', or
// 'unavailable' when the browser has made no offer.
export const promptInstall = async () => {
  const event = deferredPrompt;
  if (!event) return 'unavailable';
  // Each event can only be prompted once; Chrome sends a fresh one if the
  // app is still installable later.
  clearInstallPrompt();
  event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
};

// Register before React mounts: Chrome can fire the event straight after load.
export const listenForInstallPrompt = (win = window) => {
  win.addEventListener('beforeinstallprompt', captureInstallPrompt);
  win.addEventListener('appinstalled', clearInstallPrompt);
};

export const isRunningInstalled = (win = window) =>
  Boolean(win.matchMedia?.('(display-mode: standalone)').matches) || win.navigator.standalone === true;

// iPadOS Safari reports itself as a Mac; touch support gives it away.
export const isIOSDevice = (nav = window.navigator) =>
  /iPad|iPhone|iPod/.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);

// What to do when the browser has made no offer (Safari never does).
export const installHelpText = (nav = window.navigator) => (isIOSDevice(nav)
  ? 'To install: open this page in Safari, tap Share, then “Add to Home Screen”.'
  : 'To install: open this page in Chrome, tap the ⋮ menu, then “Install app” or “Add to Home screen”.');
